import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useToast } from '../ToastContext';
import { useAuth } from '../AuthContext';

// ── Provider icon + label ────────────────────────────────────────────────
function providerIcon(provider) {
  if (!provider) return '📬';
  const p = String(provider).toLowerCase();
  if (p === 'gmail') return '📬';
  if (p === 'microsoft' || p === 'outlook') return '📧';
  if (p === 'imap') return '🔌';
  if (p === 'ses') return '⚙';
  return '📬';
}

function providerLabel(provider) {
  if (!provider) return 'Email';
  const p = String(provider).toLowerCase();
  if (p === 'gmail') return 'Gmail';
  if (p === 'microsoft' || p === 'outlook') return 'Outlook';
  if (p === 'imap') return 'IMAP';
  if (p === 'ses') return 'AWS SES';
  return provider;
}

// ── SendFromPicker component ─────────────────────────────────────────────
/**
 * Renders a card with radio rows for each connected mailbox connector,
 * plus an optional SES row and a "Connect another mailbox" link.
 */
function SendFromPicker({ connectors, sesAvailable, value, onChange, defaultConnectorId }) {
  // Build the options list: real connectors + optional SES
  const hasConnectors = connectors.length > 0;

  // SES is the internal fallback — never offered as a user pick. If the user
  // has zero connectors AND chooses email mode, we just hide the picker;
  // server-side dispatch falls back to SES (or "manual" record-only) on its own.
  if (!hasConnectors) {
    return (
      <div
        className="card"
        style={{
          marginBottom: 16,
          border: '1px solid var(--warning)',
          background: 'var(--warning-tint)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 20 }}>📭</span>
          <div>
            <b>No mailbox connected.</b>{' '}
            <Link to="/settings/email-inbox" style={{ color: 'var(--primary)' }}>
              Connect Gmail or Outlook
            </Link>{' '}
            so suppliers see your email as the sender — or switch to Manual mode below.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginBottom: 16 }} data-testid="send-from-picker">
      <h2 className="card-title" style={{ marginBottom: 10 }}>Send from</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {connectors.map((c) => {
          const isSelected = value === c._id;
          const isDefault = String(c._id) === String(defaultConnectorId);
          const isError = c.status === 'error' || c.status === 'disconnected';
          return (
            <label
              key={c._id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 10px',
                borderRadius: 6,
                cursor: 'pointer',
                background: isSelected ? 'var(--surface-1, #f8faff)' : 'transparent',
                border: isSelected ? '1px solid var(--primary)' : '1px solid transparent',
              }}
            >
              <input
                type="radio"
                name="sendFromConnector"
                value={c._id}
                checked={isSelected}
                onChange={() => onChange(c._id)}
                style={{ accentColor: 'var(--primary)' }}
              />
              <span style={{ fontSize: 18 }}>{providerIcon(c.provider)}</span>
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>
                  {providerLabel(c.provider)}
                </span>
                <span style={{ color: 'var(--ink-500)', fontSize: 13, marginLeft: 6 }}>
                  {c.displayEmail || c.email || ''}
                </span>
                {isDefault && (
                  <span className="chip info" style={{ fontSize: 10, marginLeft: 6, padding: '2px 6px' }}>
                    default
                  </span>
                )}
              </div>
              {isError && (
                <span className="chip review" style={{ fontSize: 11 }}>
                  ⚠ {c.status}
                </span>
              )}
            </label>
          );
        })}

        {/* AWS SES is intentionally hidden from the user-facing picker.
            If no connector is connected, server falls back to SES internally. */}
      </div>

      <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border-1)' }}>
        <Link
          to="/settings/email-inbox"
          style={{ fontSize: 13, color: 'var(--primary)', textDecoration: 'none' }}
        >
          Connect another mailbox →
        </Link>
      </div>
    </div>
  );
}

// ── Main Dispatch component ───────────────────────────────────────────────
export default function Dispatch({ data, reload, preselectedLineIds = [], onClearPreselect }) {
  const { user, updateUser } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [envelopes, setEnvelopes] = useState([]);
  const [batches, setBatches] = useState([]);
  // selected suppliers
  const [selected, setSelected] = useState({});
  // supplierId -> Set<contactIdx> — contacts to include in the email.
  // Index 0 (primary) goes in To; the rest go in Cc.
  const [selectedContacts, setSelectedContacts] = useState({});
  // which lines are in scope for the new dispatch
  const [scopedLineIds, setScopedLineIds] = useState(new Set(preselectedLineIds));
  const [preview, setPreview] = useState(null);
  const [viewingIdx, setViewingIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [method, setMethod] = useState('email');
  // User-editable email text. When `edited` is true, editSubject/editBody are
  // sent verbatim to every selected supplier (shared-edit model).
  const [edited, setEdited] = useState(false);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const toast = useToast();

  // ── Connector state ─────────────────────────────────────────────────────
  const [connectors, setConnectors] = useState([]);
  const [connectorsLoading, setConnectorsLoading] = useState(false);
  const [selectedConnectorId, setSelectedConnectorId] = useState(null);

  const load = useCallback(async () => {
    const [s, e, b] = await Promise.all([
      api.listSuppliers(),
      api.listEnvelopes(data.rfq._id),
      api.listDispatchBatches(data.rfq._id),
    ]);
    setSuppliers(s.items);
    setEnvelopes(e.items);
    setBatches(b.items || []);
    // pre-tick any supplier already sent to (per existing envelopes) — but allow re-dispatch
    const pre = {};
    for (const env of e.items) pre[String(env.supplierId)] = false; // default unchecked for new dispatch
    setSelected(pre);
  }, [data.rfq._id]);

  useEffect(() => { load(); }, [load]);

  // Load connectors on mount
  useEffect(() => {
    setConnectorsLoading(true);
    api.listConnectors()
      .then((res) => {
        const items = res?.items || res || [];
        // Only show connected connectors
        const connected = items.filter((c) => c.status === 'connected');
        setConnectors(connected);
      })
      .catch(() => setConnectors([]))
      .finally(() => setConnectorsLoading(false));
  }, []);

  // Set default connector selection when connectors or preview load
  useEffect(() => {
    if (connectorsLoading) return;
    // Default priority: user preference → first connected connector → null.
    // SES is intentionally NOT a user-pickable default; backend uses it as
    // an internal fallback only.
    const prefId = user?.preferences?.defaultDispatchConnectorId;
    if (prefId && connectors.find((c) => String(c._id) === String(prefId))) {
      setSelectedConnectorId(String(prefId));
    } else if (connectors.length > 0) {
      setSelectedConnectorId(String(connectors[0]._id));
    } else {
      setSelectedConnectorId(null);
    }
  }, [connectors, connectorsLoading, user?.preferences?.defaultDispatchConnectorId]); // eslint-disable-line react-hooks/exhaustive-deps

  // when preselectedLineIds changes (coming in from Lines tab), update scopedLineIds
  useEffect(() => {
    if (preselectedLineIds.length > 0) {
      setScopedLineIds(new Set(preselectedLineIds));
    }
  }, [preselectedLineIds.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggle(id) {
    setSelected((s) => ({ ...s, [id]: !s[id] }));
    setSelectedContacts((sc) => {
      // First time selecting this supplier — default to primary contact (idx 0)
      if (sc[id] === undefined) return { ...sc, [id]: new Set([0]) };
      return sc;
    });
  }
  function toggleContact(supplierId, idx) {
    setSelectedContacts((sc) => {
      const cur = new Set(sc[supplierId] || [0]);
      if (cur.has(idx)) cur.delete(idx);
      else cur.add(idx);
      return { ...sc, [supplierId]: cur };
    });
  }
  const selectedIds = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);

  // line scope: if empty treat as whole-RFQ
  function toggleLine(id) {
    setScopedLineIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function clearScope() { setScopedLineIds(new Set()); onClearPreselect && onClearPreselect(); }
  function selectAllLines() { setScopedLineIds(new Set(data.lines.map((l) => String(l._id)))); }

  async function loadPreview() {
    if (selectedIds.length === 0) { setPreview(null); return; }
    try {
      const body = { supplierIds: selectedIds };
      if (scopedLineIds.size > 0) body.lineItemIds = [...scopedLineIds];
      if (selectedConnectorId) body.connectorId = selectedConnectorId;
      const r = await api.post(`/v1/rfqs/${data.rfq._id}/dispatch/preview`, body);
      setPreview(r);
      setViewingIdx(0);
      // Seed the editable fields from the fresh preview UNLESS the user is
      // mid-edit (don't clobber their text when scope/connector changes).
      if (!edited) {
        const first = r?.previews?.[0];
        if (first) { setEditSubject(first.subject || ''); setEditBody(first.body || ''); }
      }
    } catch (e) { toast.push(e.message, { variant: 'error' }); }
  }

  useEffect(() => { loadPreview(); }, [JSON.stringify(selectedIds), JSON.stringify([...scopedLineIds].sort()), selectedConnectorId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve display email for the chosen connector (for preview "From:" line)
  const chosenConnector = selectedConnectorId && selectedConnectorId !== 'ses'
    ? connectors.find((c) => String(c._id) === selectedConnectorId)
    : null;
  const fromEmail = chosenConnector?.displayEmail || chosenConnector?.email || 'admin@cnerzy.com';

  async function send() {
    if (selectedIds.length === 0) return;
    setBusy(true);
    try {
      const lineIds = scopedLineIds.size > 0 ? [...scopedLineIds] : undefined;
      // Build cc list per supplier (contacts beyond the primary that the user kept ticked)
      const ccBySupplier = {};
      for (const sId of selectedIds) {
        const supplier = suppliers.find((s) => String(s._id) === String(sId));
        const contacts = (supplier?.contacts || []).filter((c) => c.email);
        const sel = selectedContacts[sId] || new Set([0]);
        const cc = [];
        contacts.forEach((c, idx) => {
          if (idx > 0 && sel.has(idx)) cc.push(c.email);
        });
        if (cc.length > 0) ccBySupplier[sId] = cc;
      }
      const dispatchOpts = edited
        ? { customSubject: editSubject, customBody: editBody }
        : {};
      const r = await api.dispatch(data.rfq._id, selectedIds, lineIds, method, selectedConnectorId || undefined, ccBySupplier, dispatchOpts);
      const emailed = r.sent.filter((x) => x.sent).length;
      const failed = r.sent.filter((x) => x.error);

      if (r.method === 'email') {
        const senderLabel = chosenConnector
          ? `${providerLabel(chosenConnector.provider)} (${fromEmail})`
          : 'CNERZY';

        if (failed.length > 0) {
          const detail = failed
            .slice(0, 2)
            .map((f) => `${f.envelope?.supplier?.name || 'supplier'}: ${f.error}`)
            .join(' · ');
          toast.push(
            `Sent from ${senderLabel} to ${emailed} of ${r.sent.length} · ${failed.length} failed (${detail})`,
            { variant: failed.length === r.sent.length ? 'error' : 'warning' }
          );
        } else {
          toast.push(`Sent from ${senderLabel} to ${emailed} supplier(s)`);
        }

        // Persist the chosen connector as the user's default (fire-and-forget)
        if (selectedConnectorId && selectedConnectorId !== 'ses') {
          api.updatePreferences({ defaultDispatchConnectorId: selectedConnectorId })
            .then(() => {
              // Optimistically update the in-memory user preferences
              if (updateUser) {
                updateUser({ preferences: { ...(user?.preferences || {}), defaultDispatchConnectorId: selectedConnectorId } });
              }
            })
            .catch(() => {}); // silently ignore — backend may not have the endpoint yet
        }
      } else {
        toast.push(`Marked ${r.sent.length} sent (manual mode)`);
      }

      setScopedLineIds(new Set());
      onClearPreselect && onClearPreselect();
      await load();
      reload();
    } catch (e) { toast.push(e.message, { variant: 'error' }); }
    finally { setBusy(false); }
  }

  const chosen = preview?.previews?.[viewingIdx];
  const scopeLabel = scopedLineIds.size > 0
    ? `${scopedLineIds.size} of ${data.lines.length} lines`
    : `All ${data.lines.length} lines`;

  // Build send button label
  function sendButtonLabel() {
    if (busy) return 'Sending…';
    if (method === 'manual') return `📋 Mark ${selectedIds.length} as sent`;
    if (chosenConnector) {
      const email = chosenConnector.displayEmail || chosenConnector.email || '';
      return `📨 Send from ${providerLabel(chosenConnector.provider)}${email ? ` · ${email}` : ''} (${selectedIds.length} supplier(s))`;
    }
    // No user-pickable connector AND user hasn't switched to manual: server uses
    // the cnerzy-managed fallback. Don't expose "SES" to the user.
    return `📨 Send to ${selectedIds.length} supplier(s)`;
  }

  // Whether "email" method is usable: connector OR SES (SES is internal-only).
  const canEmail = connectors.length > 0 || preview?.sesAvailable;

  return (
    <div>
      {/* ── Dispatch batches ──────────────────────────────────────────── */}
      {batches.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h2 className="card-title">Dispatch batches ({batches.length})</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {batches.map((batch) => {
              const batchEnvs = envelopes.filter((e) => String(e.dispatchBatchId) === String(batch._id));
              return (
                <div key={batch._id} style={{ padding: '10px 12px', background: 'var(--surface-1)', borderRadius: 8, border: '1px solid var(--border-1)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>
                      {new Date(batch.dispatchedAt || batch.createdAt).toLocaleString()}
                    </div>
                    <span className="chip info">{batch.method || 'email'}</span>
                    <div style={{ flex: 1 }} />
                    <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>
                      {batchEnvs.length} supplier{batchEnvs.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {/* Line scope chips */}
                  <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {batch.lineItemIds && batch.lineItemIds.length > 0 ? (
                      <>
                        <span style={{ fontSize: 11, color: 'var(--ink-500)', marginRight: 4 }}>Lines:</span>
                        {batch.lineItemIds.map((lid) => {
                          const line = data.lines.find((l) => String(l._id) === String(lid));
                          return line
                            ? <span key={lid} className="chip muted" style={{ fontSize: 11 }}>#{line.lineNumber} {line.partNumber}</span>
                            : <span key={lid} className="chip muted" style={{ fontSize: 11 }}>{String(lid).slice(-6)}</span>;
                        })}
                      </>
                    ) : (
                      <span className="chip muted" style={{ fontSize: 11 }}>All lines</span>
                    )}
                    {/* Supplier chips */}
                    {batchEnvs.map((env) => (
                      <span key={env._id} className={'chip ' + (env.responseStatus === 'Responded' ? 'ready' : env.responseStatus === 'Declined' ? 'risk' : 'review')} style={{ fontSize: 11 }}>
                        {env.supplier?.name || String(env.supplierId).slice(-6)}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── New dispatch ──────────────────────────────────────────────── */}
      <div className="card section-bg" style={{ marginBottom: 16 }}>
        <h2 className="card-title">New dispatch</h2>

        {/* Line scope selector */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            Line scope: <span className="chip info" style={{ marginLeft: 4 }}>{scopeLabel}</span>
            {scopedLineIds.size > 0 && (
              <button className="btn btn-ghost btn-sm" style={{ marginLeft: 8 }} onClick={clearScope}>Use all lines</button>
            )}
            {scopedLineIds.size === 0 && data.lines.length > 0 && (
              <button className="btn btn-ghost btn-sm" style={{ marginLeft: 8 }} onClick={selectAllLines}>Select all</button>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {data.lines.map((l) => {
              const inScope = scopedLineIds.size === 0 || scopedLineIds.has(String(l._id));
              return (
                <span
                  key={l._id}
                  className={'chip ' + (inScope && scopedLineIds.size > 0 ? 'primary' : 'muted')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => toggleLine(String(l._id))}
                  title={inScope ? 'Click to remove from scope' : 'Click to add to scope'}
                >
                  #{l.lineNumber} {l.partNumber}
                  {scopedLineIds.size > 0 && (
                    <span style={{ marginLeft: 4, opacity: 0.7 }}>{inScope ? '×' : '+'}</span>
                  )}
                </span>
              );
            })}
          </div>
        </div>

        {/* Method selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
          <div>
            <b>Dispatch method</b>
            <div className="subtitle">
              {connectors.length > 0
                ? 'Send the dispatch email from your connected mailbox.'
                : canEmail
                ? 'No mailbox connected yet — emails will go through CNERZY default sender. Connect Gmail/Outlook to send from your address.'
                : 'No email sender configured — use Manual or connect a mailbox.'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <span
              className={'chip ' + (method === 'email' ? 'primary' : 'muted')}
              style={{ cursor: canEmail ? 'pointer' : 'not-allowed', opacity: canEmail ? 1 : 0.5 }}
              onClick={() => canEmail && setMethod('email')}
            >📨 Send via email</span>
            <span
              className={'chip ' + (method === 'manual' ? 'primary' : 'muted')}
              style={{ cursor: 'pointer' }}
              onClick={() => setMethod('manual')}
            >📋 Manual (copy &amp; paste)</span>
          </div>
          <div style={{ flex: 1 }} />
          {preview?.attachment && (
            <div className="chip info">📎 {preview.attachment.filename} · {Math.round(preview.attachment.size / 1024)} KB</div>
          )}
        </div>
      </div>

      {/* ── Send-from picker (email mode only) ───────────────────────── */}
      {method === 'email' && !connectorsLoading && (
        <SendFromPicker
          connectors={connectors}
          sesAvailable={!!preview?.sesAvailable}
          value={selectedConnectorId}
          onChange={setSelectedConnectorId}
          defaultConnectorId={user?.preferences?.defaultDispatchConnectorId}
        />
      )}

      <div className="row" style={{ alignItems: 'flex-start' }}>
        <div className="card" style={{ flex: 2 }}>
          <h2 className="card-title">Suppliers ({selectedIds.length} selected)</h2>
          {suppliers.map((s) => {
            const contacts = (s.contacts || []).filter((c) => c.email);
            const sId = String(s._id);
            const isChecked = !!selected[s._id];
            const sel = selectedContacts[sId] || new Set([0]);
            return (
              <div key={s._id} style={{ padding: 8, borderBottom: '1px solid var(--border-1)' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <input type="checkbox" checked={isChecked} onChange={() => toggle(s._id)} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{s.name}</div>
                    <div style={{ color: 'var(--ink-500)', fontSize: 12 }}>
                      {(s.category || []).join(', ')}
                      {contacts[0]?.email && <> · {contacts[0].email}</>}
                    </div>
                  </div>
                  {envelopes.some((e) => String(e.supplierId) === String(s._id)) && (
                    <span className="chip info" title="Previously dispatched">Sent {envelopes.filter((e) => String(e.supplierId) === String(s._id)).length}x</span>
                  )}
                  {!contacts[0]?.email && <span className="chip review" title="No email address">no email</span>}
                </label>
                {isChecked && contacts.length > 1 && (
                  <div style={{ marginTop: 6, marginLeft: 28, padding: 8, background: 'var(--surface-1)', borderRadius: 6, fontSize: 12 }}>
                    <div style={{ color: 'var(--ink-500)', marginBottom: 4 }}>
                      Send to (primary → To, others → Cc):
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {contacts.map((c, idx) => (
                        <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input
                            type="checkbox"
                            checked={sel.has(idx)}
                            disabled={idx === 0}
                            onChange={() => toggleContact(sId, idx)}
                          />
                          <span style={{ fontWeight: idx === 0 ? 600 : 400 }}>{c.name || '—'}</span>
                          <span className="mono" style={{ color: 'var(--ink-500)' }}>{c.email}</span>
                          {idx === 0 && <span className="chip muted" style={{ fontSize: 10 }}>primary · To</span>}
                          {idx > 0 && sel.has(idx) && <span className="chip muted" style={{ fontSize: 10 }}>Cc</span>}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="card section-bg" style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h2 className="card-title" style={{ margin: 0 }}>Preview</h2>
            <div style={{ flex: 1 }} />
            {preview?.previews?.length > 0 && (
              edited ? (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    // Revert to the auto-generated text for the current supplier.
                    setEdited(false);
                    const first = preview?.previews?.[0];
                    if (first) { setEditSubject(first.subject || ''); setEditBody(first.body || ''); }
                  }}
                  title="Discard edits and use the auto-generated email"
                >↺ Reset to default</button>
              ) : (
                <button className="btn btn-secondary btn-sm" onClick={() => setEdited(true)} title="Edit the subject & body before sending">
                  ✎ Edit email
                </button>
              )
            )}
          </div>
          {edited && (
            <div className="banner info" style={{ marginTop: 8, marginBottom: 4, fontSize: 12 }}>
              Editing — this exact subject &amp; body will be sent to all {selectedIds.length} selected supplier(s). Per-supplier personalization (e.g. “Hello &lt;supplier&gt;”) is not applied while editing.
            </div>
          )}
          {preview?.previews?.length ? (
            <>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                {preview.previews.map((p, i) => (
                  <span key={p.supplier._id} className={'chip ' + (i === viewingIdx ? 'primary' : 'muted')} style={{ cursor: 'pointer' }} onClick={() => setViewingIdx(i)}>
                    {p.supplier.name}
                  </span>
                ))}
              </div>
              {chosen && (
                <>
                  {/* From line: reflect chosen connector's display email */}
                  {method === 'email' && (
                    <div style={{ fontSize: 12, color: 'var(--ink-500)', marginBottom: 2 }}>
                      From: <span className="mono">
                        {selectedConnectorId === 'ses'
                          ? (preview?.fromAddress || 'admin@cnerzy.com')
                          : fromEmail}
                      </span>
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: 'var(--ink-500)', marginBottom: 4 }}>
                    To: <span className="mono">{chosen.to || '(no email on file)'}</span>
                  </div>
                  {edited ? (
                    <>
                      <label className="field" style={{ marginBottom: 8 }}>
                        <span className="label">Subject</span>
                        <input
                          className="input"
                          value={editSubject}
                          onChange={(e) => setEditSubject(e.target.value)}
                        />
                      </label>
                      <label className="field">
                        <span className="label">Body</span>
                        <textarea
                          className="input mono"
                          style={{ fontSize: 12, minHeight: 300, lineHeight: 1.5 }}
                          value={editBody}
                          onChange={(e) => setEditBody(e.target.value)}
                        />
                      </label>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Subject: {chosen.subject}</div>
                      <pre className="mono" style={{ fontSize: 12, whiteSpace: 'pre-wrap', background: '#fff', padding: 12, borderRadius: 6, border: '1px solid var(--border-1)', maxHeight: 360, overflow: 'auto' }}>{chosen.body}</pre>
                    </>
                  )}
                  {preview.attachment && (
                    <div style={{ fontSize: 11, color: 'var(--ink-500)', marginTop: 6 }}>Attachment: <b>{preview.attachment.filename}</b> (Excel with line items + supplier response columns)</div>
                  )}
                </>
              )}
            </>
          ) : (
            <div className="empty">Pick at least one supplier to preview.</div>
          )}

          <button
            className="btn btn-accent"
            style={{ marginTop: 16, width: '100%' }}
            onClick={send}
            disabled={busy || selectedIds.length === 0}
            data-testid="dispatch-send-btn"
          >
            {sendButtonLabel()}
          </button>
        </div>
      </div>
    </div>
  );
}
